import type { RTNode } from "@prismicio/client"
import classNames from "classnames"
import { useRouter } from "next/router"
import * as React from "react"
import { useSwiperSlide } from "swiper/react"

import { Analytics } from "@bounce/analytics"
import { Paragraph2 } from "@bounce/design-system"
import { useTranslation } from "@bounce/i18n"
import type { PNImageProps } from "@bounce/web-components"
import { PNImage, useIsMobile } from "@bounce/web-components"
import { Truncate } from "@bounce/web-components/src/DataDisplay/Truncate/Truncate"

import SafeLink from "../SafeLink/SafeLink"
import { InteractionEvents } from "@/libs/analytics/events"

import defaultImage from "@/assets/img/default_hero_1.png"

export type PlaceCardProps = {
  appUrl: string
  className?: string
  content?: RTNode
  href?: string
  image: PNImageProps["src"]
  imageClassName?: string
  imageContainerClassName?: string
  name: string
  titleClassName?: string
}

const HEIGHT = 430
const WIDTH = 323

export const PlaceCard = ({
  appUrl,
  className,
  content,
  href,
  image,
  imageClassName,
  imageContainerClassName,
  name,
  titleClassName,
}: PlaceCardProps) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { isVisible, isActive } = useSwiperSlide()
  const isMobile = useIsMobile()

  const onClick = (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    ev.preventDefault()
    void router.push(appUrl)
  }

  return (
    <>
      <SafeLink
        href={href ?? appUrl}
        className={classNames(
          "flex flex-col transition-all duration-300 ease-in-out",
          isActive ? "scale-100" : "scale-[85%]",
          className,
        )}
        onClick={onClick}>
        <div
          className={classNames(
            "h-[350px] transition-all duration-300 ease-in-out sm:h-[430px]",
            imageContainerClassName,
          )}>
          {isVisible && (
            <PNImage
              src={image || defaultImage}
              width={isMobile ? WIDTH / 2 : WIDTH}
              height={isMobile ? HEIGHT / 2 : HEIGHT}
              quality={80}
              alt=""
              imgixParams={{
                height: isMobile ? HEIGHT * 0.75 : HEIGHT * 1.5,
                width: isMobile ? WIDTH * 0.75 : WIDTH * 1.5,
                fit: "fill",
              }}
              loading="lazy"
              data-chromatic="ignore"
              className={classNames("size-full rounded-lg object-cover", imageClassName)}
            />
          )}
        </div>
        <h3 className={classNames("title4 mt-5 line-clamp-3 text-black md:mt-6", titleClassName)}>{name}</h3>
      </SafeLink>
      {content && "text" in content && (
        <Truncate
          as="div"
          className={classNames("paragraph2", isActive ? "scale-100" : "scale-[85%]")}
          onClick={(isOpen: boolean) =>
            Analytics.track(InteractionEvents.TruncateToggled, {
              name: "PlaceCard",
              open: isOpen,
            })
          }>
          <Paragraph2 className="mt-2 text-neutral-800">{content.text}</Paragraph2>
        </Truncate>
      )}
    </>
  )
}
